import { z } from 'zod';

// Base schema for all RevenueCat webhook events
const baseEventSchema = z.object({
  id: z.string(),
  app_id: z.string(),
  environment: z.enum(['SANDBOX', 'PRODUCTION']),
  event_timestamp_ms: z.number(),
  store: z.enum(['APP_STORE', 'MAC_APP_STORE', 'PLAY_STORE', 'STRIPE', 'PROMOTIONAL']),
  subscriber_attributes: z
    .record(
      z.string(),
      z.object({
        updated_at_ms: z.number(),
        value: z.string(),
      }),
    )
    .optional(),
});

// Schema for subscription-related events (RENEWAL, INITIAL_PURCHASE, EXPIRED, etc.)
const subscriptionEventSchema = baseEventSchema.extend({
  type: z.enum([
    'INITIAL_PURCHASE',
    'RENEWAL',
    'CANCELLATION',
    'UNCANCELLATION',
    'NON_RENEWING_PURCHASE',
    'SUBSCRIPTION_PAUSED',
    'EXPIRED',
    'EXPIRATION',
    'PRODUCT_CHANGE',
  ]),
  aliases: z.array(z.string()).optional(),
  app_user_id: z.string(),
  commission_percentage: z.number().nullable().optional(),
  country_code: z.string().optional(),
  currency: z.string(),
  entitlement_id: z.string().nullable().optional(),
  entitlement_ids: z.array(z.string()).optional(),
  expiration_at_ms: z.number().nullable().optional(),
  grace_period_expiration_at_ms: z.number().nullable().optional(),
  is_family_share: z.boolean().optional(),
  is_trial_conversion: z.boolean().optional(),
  metadata: z.any().nullable().optional(),
  offer_code: z.string().nullable().optional(),
  original_app_user_id: z.string(),
  original_transaction_id: z.string(),
  period_type: z.enum(['NORMAL', 'TRIAL', 'INTRO']).optional(),
  presented_offering_id: z.string().nullable().optional(),
  price: z.number().optional(),
  price_in_purchased_currency: z.number().optional(),
  product_id: z.string(),
  purchased_at_ms: z.number().optional(),
  renewal_number: z.number().optional(),
  takehome_percentage: z.number().optional(),
  tax_percentage: z.number().nullable().optional(),
  transaction_id: z.string(),
});

// Schema for TRANSFER events
const transferEventSchema = baseEventSchema.extend({
  type: z.literal('TRANSFER'),
  transferred_from: z.array(z.string()),
  transferred_to: z.array(z.string()),
});

// Discriminated union for all event types
const eventUnionSchema = z.discriminatedUnion('type', [subscriptionEventSchema, transferEventSchema]);

// Main webhook schema
export const revenueCatWebhookSchema = z.object({
  api_version: z.string(),
  event: eventUnionSchema,
});

export type RevenueCatWebhookPayload = z.infer<typeof revenueCatWebhookSchema>;
export type SubscriptionEvent = z.infer<typeof subscriptionEventSchema>;
export type TransferEvent = z.infer<typeof transferEventSchema>;
